/**
 * blocks.ts — the GenUI block vocabulary (bounded context: `genui`).
 *
 * Instead of streaming prose, the in-app assistant expresses itself by composing
 * TYPED blocks into a shared scratchpad. Each block kind is one `arktype` type — and
 * that single definition does triple duty (exactly as the rest of the domain does):
 *   1. it validates the assistant's tool input (and a human's edit to the same block),
 *   2. it infers the static TypeScript type, and
 *   3. it projects to JSON Schema (`.toJsonSchema()`) for the field reference inside
 *      the SKILL the assistant reads — so the teaching never drifts from the code.
 *
 * The set is deliberately small and composable: nine kinds cover explanation,
 * structure, comparison, lineage, and metrics without becoming a second prose channel.
 */
import { type } from "arktype";
import { BusinessCanvasBlock } from "../domain/business.ts";

export { BusinessCanvasBlock } from "../domain/business.ts";

// ── The nine block kinds ─────────────────────────────────────────────────────────
export const Prose = type({ kind: "'prose'", text: "string" });

export const Heading = type({ kind: "'heading'", text: "string", "level?": "1 | 2 | 3" });

export const Callout = type({
  kind: "'callout'",
  tone: "'info' | 'insight' | 'warn' | 'boundary'",
  "title?": "string",
  body: "string",
});

export const Fields = type({
  kind: "'fields'",
  "title?": "string",
  rows: type({ label: "string", value: "string" }).array(),
});

export const ListBlock = type({
  kind: "'list'",
  "title?": "string",
  "ordered?": "boolean",
  items: type({ text: "string", "status?": "'todo' | 'done' | 'blocked'" }).array(),
});

export const TableBlock = type({
  kind: "'table'",
  "title?": "string",
  columns: "string[]",
  rows: "string[][]",
});

export const DiagramBlock = type({ kind: "'diagram'", "title?": "string", source: "string" });

export const EntitiesBlock = type({ kind: "'entities'", "title?": "string", ids: "string[]" });

export const StatBlock = type({
  kind: "'stat'",
  items: type({ label: "string", value: "string", "note?": "string" }).array(),
});

export const ActionsBlock = type({
  kind: "'actions'",
  "title?": "string",
  "prompt?": "string",
  options: type({ label: "string", send: "string", "tone?": "'default' | 'primary' | 'boundary'" }).array(),
});

/**
 * A group binds a section together: a titled container that OWNS the blocks belonging
 * to it (so a heading and its components read as one section), and may nest groups for
 * sub-sections. The children are validated recursively by {@link parseBlock} — the
 * arktype shell keeps them as `unknown[]` to avoid a self-referential schema, and the
 * recursion enforces that every descendant is itself a valid block.
 */
export const GroupBlock = type({
  kind: "'group'",
  "title?": "string",
  "tone?": "'plain' | 'framed' | 'accent'",
  "layout?": "'stack' | 'row' | 'grid'",
  children: "unknown[]",
});

/** The discriminated union the assistant and humans both produce (shell validation). */
export const BlockData = Prose.or(Heading)
  .or(Callout)
  .or(Fields)
  .or(ListBlock)
  .or(TableBlock)
  .or(DiagramBlock)
  .or(EntitiesBlock)
  .or(StatBlock)
  .or(ActionsBlock)
  .or(BusinessCanvasBlock)
  .or(GroupBlock);

/** A leaf block is any kind except a group. */
export type LeafBlock =
  | typeof Prose.infer
  | typeof Heading.infer
  | typeof Callout.infer
  | typeof Fields.infer
  | typeof ListBlock.infer
  | typeof TableBlock.infer
  | typeof DiagramBlock.infer
  | typeof EntitiesBlock.infer
  | typeof StatBlock.infer
  | typeof ActionsBlock.infer
  | typeof BusinessCanvasBlock.infer;

/** A group nests blocks (including other groups) — hand-written so the type is recursive. */
export interface GroupData {
  readonly kind: "group";
  readonly title?: string;
  readonly tone?: "plain" | "framed" | "accent";
  readonly layout?: "stack" | "row" | "grid";
  readonly children: readonly BlockData[];
}

export type BlockData = LeafBlock | GroupData;
export type BlockKind = BlockData["kind"];

/** One library entry per kind: schema + the rationale and field notes the SKILL teaches. */
export interface BlockSpec {
  readonly kind: BlockKind;
  readonly schema: typeof BlockData;
  readonly whenToUse: string;
  readonly rationale: string;
  /** Per-field guidance, surfaced in the SKILL alongside the generated JSON Schema. */
  readonly fields: Readonly<Record<string, string>>;
  readonly example: BlockData;
}

export const BLOCK_LIBRARY: readonly BlockSpec[] = [
  {
    kind: "heading",
    schema: Heading as unknown as typeof BlockData,
    whenToUse: "To open a section or label a group of blocks.",
    rationale: "Headings give the scratchpad scannable structure so a reader can navigate without reading every block.",
    fields: { text: "The heading text.", level: "1 = section, 3 = sub-label. Default 2." },
    example: { kind: "heading", text: "What currently exists", level: 2 },
  },
  {
    kind: "prose",
    schema: Prose as unknown as typeof BlockData,
    whenToUse: "Only for a sentence or two of connective tissue between structured blocks.",
    rationale: "Prose is the fallback, not the default — value lives in structured blocks the reader can act on.",
    fields: { text: "Connective text. Markdown-lite (inline emphasis only). Keep it short." },
    example: { kind: "prose", text: "Two interpretations are awaiting your review." },
  },
  {
    kind: "callout",
    schema: Callout as unknown as typeof BlockData,
    whenToUse: "To surface an insight, a caution, or — with tone:boundary — what an output does NOT establish.",
    rationale: "The boundary ladder requires naming limits explicitly; tone:boundary makes that a first-class, visible move.",
    fields: { tone: "info = neutral; insight = a finding; warn = caution; boundary = a governance limit.", title: "Optional bold lead-in.", body: "The callout text." },
    example: { kind: "callout", tone: "boundary", title: "Not yet established", body: "This is a candidate reading; it crosses no governed boundary until you review it." },
  },
  {
    kind: "fields",
    schema: Fields as unknown as typeof BlockData,
    whenToUse: "To show the attributes or provenance of a single thing as label→value rows.",
    rationale: "Mirrors the evidence-readout panel, so structured facts feel native and stay scannable.",
    fields: { title: "Optional panel title.", rows: "Array of {label, value} pairs." },
    example: { kind: "fields", title: "Session", rows: [{ label: "status", value: "open" }, { label: "evidence", value: "4 items" }] },
  },
  {
    kind: "list",
    schema: ListBlock as unknown as typeof BlockData,
    whenToUse: "For bullets, an ordered sequence of steps, or a checklist (per-item status).",
    rationale: "One block covers three needs; per-item status turns a list into an actionable plan.",
    fields: { title: "Optional list title.", ordered: "true = numbered steps; omit for bullets.", items: "Array of {text, status?} where status ∈ todo|done|blocked." },
    example: { kind: "list", title: "Next steps", ordered: true, items: [{ text: "Review the candidate", status: "todo" }, { text: "Author an asset", status: "todo" }] },
  },
  {
    kind: "table",
    schema: TableBlock as unknown as typeof BlockData,
    whenToUse: "To compare a few things across a few dimensions.",
    rationale: "A table is the honest shape for comparison; keep it small so it stays readable in a side panel.",
    fields: { title: "Optional table title.", columns: "Column headers.", rows: "Each row's cells, in column order." },
    example: { kind: "table", columns: ["asset", "status", "version"], rows: [["Refund judgment", "approved", "v2"]] },
  },
  {
    kind: "diagram",
    schema: DiagramBlock as unknown as typeof BlockData,
    whenToUse: "To show a flow or the boundary ladder.",
    rationale: "A native diagram (drawn in the studio's tokens) reads as part of the instrument, raising trust rather than lowering it.",
    fields: { title: "Optional diagram title.", source: "Compact DSL — `A: label` names a node, `A -> B : edgelabel` is a flow edge, `A ≠ B` a boundary edge — OR a Mermaid flowchart (`graph TD` / `A[Label] --> B` / `A -->|label| B`). Referenced nodes auto-create; renders in the studio's tokens (light/dark)." },
    example: { kind: "diagram", title: "Boundary ladder", source: "raw response -> interpreted meaning -> reviewed asset" },
  },
  {
    kind: "entities",
    schema: EntitiesBlock as unknown as typeof BlockData,
    whenToUse: "Whenever you reference workspace entities (sessions, evidence, assets, packs, projections…).",
    rationale: "IDs must never display; this resolves URNs to names with click-through, keeping the 'IDs never display' promise.",
    fields: { title: "Optional title.", ids: "Entity URNs (urn:egae:…). Rendered as name cards — never show a raw id to the user." },
    example: { kind: "entities", title: "Referenced", ids: ["urn:egae:asset:0f3a"] },
  },
  {
    kind: "stat",
    schema: StatBlock as unknown as typeof BlockData,
    whenToUse: "To headline a few counts or percentages.",
    rationale: "Numbers land faster as tiles than buried in a sentence.",
    fields: { items: "Array of {label, value, note?} metric tiles." },
    example: { kind: "stat", items: [{ label: "assets", value: "3" }, { label: "approved", value: "1" }] },
  },
  {
    kind: "actions",
    schema: ActionsBlock as unknown as typeof BlockData,
    whenToUse: "Whenever the next move is the human's. Offer the choices/decisions/proposals as buttons INSTEAD of asking in prose — do not stop and wait for a typed reply when you can present clickable options.",
    rationale: "Click-driven decisions beat free-text: they are faster, unambiguous, and keep the interaction on the scratchpad. Clicking an option sends its `send` text back to you as the next turn, so you can continue building.",
    fields: {
      title: "Optional lead-in for the choice.",
      prompt: "Optional one-line question above the buttons.",
      options: "Buttons: {label (button text), send (the message dispatched to you when clicked), tone? default|primary|boundary}. Make `send` a clear instruction to yourself, e.g. \"Author an asset from the corrected interpretation\".",
    },
    example: { kind: "actions", prompt: "How should I proceed?", options: [{ label: "Author an asset", send: "Author a draft asset from the corrected interpretation", tone: "primary" }, { label: "Gather more evidence", send: "Open the refund session and ask the next question" }] },
  },
  {
    kind: "business-canvas",
    schema: BusinessCanvasBlock as unknown as typeof BlockData,
    whenToUse: "For business-operating-model work — a Value Proposition Canvas or full Business Model Canvas. Use this TYPED component instead of assembling a canvas from generic fields/groups: it enforces the relationships (a value proposition serves segments; a pain reliever addresses pains) and renders the real canvas layout, colour-coded per customer segment.",
    rationale: "Generic blocks under-constrain a canvas — the fit between pains and relievers is only implied. This component makes segments, value propositions, and their links first-class, so the artifact reads as a canvas and supports many customer segments (ICPs) at once, each mapped through the nine areas.",
    fields: {
      title: "Optional canvas title.",
      view: "model = the nine-area Business Model Canvas; value-proposition = the zoomed Customer Profile ↔ Value Map. Default model.",
      model: "The business model: { segments[], valuePropositions[], channels[], customerRelationships[], revenueStreams[], keyResources[], keyActivities[], keyPartnerships[], costStructure[] }. Each segment owns jobs/pains/gains (each an {id,text}); each value proposition lists segmentIds it serves plus products/painRelievers/gainCreators whose `addresses` link to the pain/gain ids they resolve; every area entry may carry `segmentIds` to map it to the segment(s) it serves, and `state` (open|candidate|filled) so the participant can fill blanks, accept candidates, or grade.",
    },
    example: {
      kind: "business-canvas",
      title: "Acme — value proposition",
      view: "value-proposition",
      model: {
        title: "Acme",
        segments: [
          { id: "seg-ops", name: "Ops leads", tone: 0, profile: "Heads of operations at mid-market firms", jobs: [{ id: "job-1", text: "Keep delivery predictable" }], pains: [{ id: "pain-1", text: "Firefighting eats the week" }], gains: [{ id: "gain-1", text: "Calm, on-time delivery" }] },
        ],
        valuePropositions: [
          { id: "vp-1", name: "Predictable delivery", segmentIds: ["seg-ops"], products: [{ id: "pr-1", text: "Flow dashboard" }], painRelievers: [{ id: "rel-1", text: "Surfaces bottlenecks early", addresses: ["pain-1"] }], gainCreators: [{ id: "gc-1", text: "Forecasts ship dates", addresses: ["gain-1"] }] },
        ],
        channels: [], customerRelationships: [], revenueStreams: [], keyResources: [], keyActivities: [], keyPartnerships: [], costStructure: [],
      },
    },
  },
  {
    kind: "group",
    schema: GroupBlock as unknown as typeof BlockData,
    whenToUse: "To bind a section together — put a section's heading (or title) and the blocks that belong to it inside ONE group so they read as a unit. Nest groups for sub-sections.",
    rationale: "A loose heading floating above unrelated blocks reads as disconnected. A group makes the section a real container — it owns its blocks, can be laid out as a column/row/grid, and can nest, which is how the document gains structure and the app gains composability.",
    fields: {
      title: "The section title (rendered as the group's heading).",
      tone: "plain (no frame) · framed (subtle border) · accent (leading rule). Default framed.",
      layout: "stack (vertical, default) · row (side by side) · grid (auto-fit columns).",
      children: "An array of blocks of ANY kind — including nested `group` blocks for sub-sections.",
    },
    example: {
      kind: "group",
      title: "Workspace snapshot",
      tone: "framed",
      children: [
        { kind: "stat", items: [{ label: "open sessions", value: "9" }] },
        { kind: "callout", tone: "boundary", body: "Nothing here has crossed into approved runtime material." },
      ],
    },
  },
];

/** Validate an unknown value as block data; returns the typed block or an error string.
 * Groups validate recursively — the shell is checked by arktype, then every child is
 * parsed in turn, so an invalid descendant rejects the whole group (with the reason). */
export function parseBlock(value: unknown): { ok: true; data: BlockData } | { ok: false; error: string } {
  const out = BlockData(value);
  if (out instanceof type.errors) return { ok: false, error: out.summary };
  if ((out as { kind: string }).kind === "group") {
    const shell = out as { kind: "group"; title?: string; tone?: GroupData["tone"]; layout?: GroupData["layout"]; children: unknown[] };
    const children: BlockData[] = [];
    for (const child of shell.children) {
      const r = parseBlock(child);
      if (!r.ok) return { ok: false, error: `group child — ${r.error}` };
      children.push(r.data);
    }
    return { ok: true, data: { ...shell, children } as BlockData };
  }
  return { ok: true, data: out as BlockData };
}
